import { Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import {
  WorkMontageplanTeamDto,
  WorkMontageplanUsersDto,
  WorkMontageplanUsersTeamPartial,
  WorkMontageplanV2Service,
  WorkPermissionsEnum,
  WorkTeam,
  WorkTimerecordtypeNameEnum,
} from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';
import { ExceptionHandlingService } from '../../services/exception-handling/exception-handling.service';
import dayjs from 'dayjs';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../button/button.component';
import { AvatarComponent } from '../avatar/avatar.component';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { PrependServerToFileSrcPipe } from '../../pipes/prepend-server-to-file-src.pipe';
import { ConfigurationKeyNamesEnum } from '../../enums/configuration-key-names.enum';
import { environment } from '@work-frontend-monorepo/work-environments';
import { Store } from '@ngxs/store';
import { ProfileState } from '../../state/states/profile/profile-state';
import { ModalComponent } from '../modal/modal.component';
import { LoadingAndFeedbackState } from '../../models/loading-and-feedback-state.type';
import { MontageplanNoteModalComponent, MontageplanNoteSavedEvent } from '../montageplan-note-modal/montageplan-note-modal.component';

@Component({
  standalone: true,
  imports: [
    TranslateModule,
    ButtonComponent,
    AvatarComponent,
    ModalComponent,
    MontageplanNoteModalComponent,
    FormatDatePipe,
    PrependServerToFileSrcPipe,
  ],
  selector: 'work-ui-montageplan-day-overview',
  templateUrl: './montageplan-day-overview.component.html',
  styleUrls: ['./montageplan-day-overview.component.scss'],
})
export class MontageplanDayOverviewComponent {
  private readonly workMontageplanV2Service = inject(WorkMontageplanV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly store = inject(Store);

  public readonly date = input.required<string>();
  public readonly teamsActive = input.required<WorkTeam[]>();
  // Opt-in: Personen für den angezeigten Tag in ein anderes Team verschieben (nur mit montageplan:update).
  public readonly allowChangeTeamOfMember = input(false);
  // Opt-in: Notizen auf den Timerecords anzeigen und bearbeiten (nur mit montageplan:notes) — nicht auf den TV-Screens.
  public readonly showMontageplanNotes = input(false);

  public readonly openProjectDetails = output<number>();
  public readonly openUserDetails = output<number>();

  // `isLoading` is consumed by parent components via viewChildren() + computed() to
  // aggregate child loading state — must stay public.
  public readonly isLoading = signal<boolean>(true);
  protected readonly montageplanTeamDtos = signal<WorkMontageplanTeamDto[]>([]);
  protected readonly teamMembers = signal<WorkMontageplanUsersDto>({ teams: {} });
  protected readonly showObjectAddressInsteadOfCustomer = signal(environment.showObjectAddressInsteadOfCustomer);

  private readonly changeTeamModal = viewChild<ModalComponent>('changeTeamModal');
  private readonly hasMontageplanUpdatePermission = this.store.selectSignal(
    ProfileState.hasPermission(WorkPermissionsEnum.MontageplanUPDATE, true),
  );
  protected readonly canChangeTeamOfMember = computed(() => this.allowChangeTeamOfMember() && this.hasMontageplanUpdatePermission());
  private readonly montageplanNoteModal = viewChild<MontageplanNoteModalComponent>('montageplanNoteModal');
  private readonly hasMontageplanNotesPermission = this.store.selectSignal(
    ProfileState.hasPermission(WorkPermissionsEnum.MontageplanNOTES),
  );
  protected readonly canEditNotes = computed(() => this.showMontageplanNotes() && this.hasMontageplanNotesPermission());
  protected readonly memberToChangeTeam = signal<{ userId: number; name: string; teamId: number } | undefined>(undefined);
  protected readonly changeTeamTargetTeams = computed(() => {
    const member = this.memberToChangeTeam();
    return member ? this.montageplanTeamDtos().filter((teamDto) => teamDto.teamId !== member.teamId) : [];
  });
  protected readonly changeTeamLoadingState = signal<LoadingAndFeedbackState>('none');

  private readonly loadOnInputChangeEffect = effect(() => {
    const teams = this.teamsActive();
    // Track date as a dependency so the effect re-fires when either input changes.
    this.date();
    if (teams.length === 0) {
      this.montageplanTeamDtos.set([]);
      return;
    }
    void this.loadDataForSelectedTeams();
  });

  protected readonly WorkTimerecordtypeNameEnum = WorkTimerecordtypeNameEnum;
  protected readonly ConfigurationKeyNamesEnum = ConfigurationKeyNamesEnum;

  private async loadDataForSelectedTeams() {
    this.isLoading.set(true);
    const activeTeamIdsAsString = this.teamsActive().map((team) => `${team.id}`);
    await this.loadTeamMembers(activeTeamIdsAsString);
    try {
      const data = await lastValueFrom(
        this.workMontageplanV2Service.montageplanV2ControllerGetMontageplanBlocksByTeamIdAndDaterange({
          teamIds: activeTeamIdsAsString,
          fromDate: dayjs(this.date()).format('YYYY-MM-DD'),
          toDate: dayjs(this.date()).format('YYYY-MM-DD'),
        }),
      );
      if (data && data.length) {
        this.montageplanTeamDtos.set(data[0].teams);
      }
    } catch (loadMontageplanDataException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadMontageplanDataException', loadMontageplanDataException);
    }
    this.isLoading.set(false);
  }

  protected isEntireTeamAbsent(montageplanUsersTeamPartials: WorkMontageplanUsersTeamPartial[] = [], date: string) {
    let isAbsent = true;
    montageplanUsersTeamPartials.forEach((usersTeamPartial) => {
      if (
        usersTeamPartial &&
        usersTeamPartial.timerecords &&
        usersTeamPartial.timerecords[date] &&
        usersTeamPartial.timerecords[date].timerecordtypeName === WorkTimerecordtypeNameEnum.Arbeit
      ) {
        isAbsent = false;
      }
    });
    return isAbsent;
  }

  protected openNoteModal(member: WorkMontageplanUsersTeamPartial) {
    this.montageplanNoteModal()?.open({
      userId: member.userId,
      name: `${member.user.firstName} ${member.user.lastName}`,
      date: this.date(),
      note: member.timerecords[this.date()]?.montageplanNote,
    });
  }

  protected onNoteSaved(event: MontageplanNoteSavedEvent) {
    const current = this.teamMembers();
    const updatedTeams = { ...current.teams };
    for (const teamId of Object.keys(updatedTeams)) {
      updatedTeams[teamId] = (updatedTeams[teamId] ?? []).map((member) =>
        member.userId === event.userId && member.timerecords[event.date]
          ? {
              ...member,
              timerecords: { ...member.timerecords, [event.date]: { ...member.timerecords[event.date], montageplanNote: event.note } },
            }
          : member,
      );
    }
    this.teamMembers.set({ teams: updatedTeams });
  }

  protected openChangeTeamModal(userId: number, name: string, teamId: number) {
    this.memberToChangeTeam.set({ userId, name, teamId });
    this.changeTeamLoadingState.set('none');
    this.changeTeamModal()?.open();
  }

  protected async changeTeamOfMember(targetTeamId: number) {
    const member = this.memberToChangeTeam();
    if (!member) {
      return;
    }
    this.changeTeamLoadingState.set('loading');
    try {
      // dateFrom = dateTo: die Umteilung betrifft ausschliesslich den angezeigten Tag.
      await lastValueFrom(
        this.workMontageplanV2Service.montageplanV2ControllerAssignUserToTeam({
          userId: member.userId,
          teamId: targetTeamId,
          dateFrom: dayjs(this.date()).format('YYYY-MM-DD'),
          dateTo: dayjs(this.date()).format('YYYY-MM-DD'),
          isTeamlead: false,
        }),
      );
      this.changeTeamLoadingState.set('success');
      await this.changeTeamModal()?.close();
      this.memberToChangeTeam.set(undefined);
      await this.loadTeamMembers(this.teamsActive().map((team) => `${team.id}`));
    } catch (changeTeamOfMemberException) {
      this.changeTeamLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('changeTeamOfMemberException', changeTeamOfMemberException);
    }
  }

  private async loadTeamMembers(teamIds: string[]) {
    if (teamIds.length === 0) {
      return;
    }
    try {
      const newMembers = await lastValueFrom(
        this.workMontageplanV2Service.montageplanV2ControllerGetMontageplanUsersByTeamIdAndDaterange({
          fromDate: dayjs(this.date()).format('YYYY-MM-DD'),
          toDate: dayjs(this.date()).format('YYYY-MM-DD'),
          teamIds: teamIds,
        }),
      );
      const current = this.teamMembers();
      const updatedTeams = { ...current.teams };
      teamIds.forEach((teamId) => {
        updatedTeams[teamId] = newMembers.teams[teamId];
      });
      this.teamMembers.set({ teams: updatedTeams });
    } catch (loadTeamsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadTeamsException', loadTeamsException);
    }
  }
}
